import React, { useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { QRCodeSVG } from 'qrcode.react';

export default function BulkQRPrintModal({ open, onClose, rooms, tenantId }) {
    const printRef = useRef(null);

    // Filter out archived rooms if needed, or just use what's passed
    const activeRooms = rooms.filter(r => r.status !== 'Archive');

    const handlePrint = () => {
        const printContent = printRef.current.innerHTML;
        const printWindow = window.open('', '', 'height=800,width=1000');
        
        printWindow.document.write('<html><head><title>Print QR Codes</title>');
        printWindow.document.write(`
            <style>
                body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                .grid-container { 
                    display: grid; 
                    grid-template-columns: repeat(3, 1fr); 
                    gap: 30px; 
                }
                .qr-card { 
                    text-align: center; 
                    padding: 20px; 
                    border: 2px dashed #ddd; 
                    border-radius: 12px; 
                    page-break-inside: avoid; 
                }
                h3 { margin: 0 0 10px 0; font-size: 24px; color: #333; }
                p { margin: 0 0 20px 0; color: #666; font-size: 16px; }
                .qr-svg { width: 100%; height: auto; max-width: 200px; margin: 0 auto; }
                
                @media print {
                    body { padding: 0; }
                    .grid-container { gap: 20px; }
                    .qr-card { border: 1px solid #ccc; }
                }
            </style>
        `);
        printWindow.document.write('</head><body>');
        printWindow.document.write('<div class="grid-container">');
        printWindow.document.write(printContent);
        printWindow.document.write('</div>');
        printWindow.document.write('</body></html>');
        
        printWindow.document.close();
        printWindow.focus();
        
        // Wait for SVGs to render fully in the new window
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 500);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Print All Room QR Codes</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            
            <DialogContent dividers sx={{ bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Previewing {activeRooms.length} QR codes. Click print to send them to your printer. They are formatted to print 3 across the page.
                </Typography>
                
                <Box ref={printRef} sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 3 }}>
                    {activeRooms.map(room => {
                        const currentHost = window.location.host;
                        const isLocalhost = currentHost.includes('localhost');
                        const guestHost = isLocalhost ? 'guest.localhost:3000' : currentHost.replace(/^(admin|pms)/, 'guest');
                        const protocol = window.location.protocol;
                        const qrUrl = `${protocol}//${guestHost}/verify?token=r_${room.id}&tenantId=${tenantId || 1}`;
                        
                        return (
                            <Box key={room.id} className="qr-card" sx={{ bgcolor: 'white', p: 3, borderRadius: 2, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <h3>Room {room.roomNumber || room.number}</h3>
                                <p>Scan for Room Service</p>
                                <Box className="qr-svg">
                                    <QRCodeSVG value={qrUrl} size={180} level="H" includeMargin={true} style={{ width: '100%', height: 'auto' }} />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} size="large">
                    Print {activeRooms.length} QR Codes
                </Button>
            </DialogActions>
        </Dialog>
    );
}
